import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";

/**
 * Standalone reason dialog for customer actions.
 * Renders above the page; the parent should hide the customer modal while open
 * so it never stacks on ModalContainer / MUI Modal.
 */
export default function CustomerReasonDialog({
  open,
  title,
  description,
  placeholder,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  onClose,
  onConfirm,
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 p-4 font-main"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-reason-dialog-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close"
        onClick={() => !loading && onClose?.()}
      />
      <div className="relative z-[1] flex w-full max-w-[440px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-primary1 px-4 py-3.5 text-white">
          <h2 id="customer-reason-dialog-title" className="m-0 text-base font-semibold sm:text-lg">
            {title}
          </h2>
          <button
            type="button"
            onClick={() => !loading && onClose?.()}
            className="rounded-md p-0.5 text-white/90 hover:bg-white/10"
            aria-label="Close"
          >
            <Icon icon="material-symbols:close" className="text-2xl" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          {description ? <p className="m-0 text-sm leading-snug text-[#5A6478]">{description}</p> : null}
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-[#111827]">
            Reason
            <textarea
              className="min-h-[104px] w-full rounded-lg border border-[#D6DEEA] px-3 py-2 text-sm font-normal text-[#111827] outline-none focus:border-primary1"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={placeholder}
              maxLength={500}
              disabled={loading}
              autoFocus
            />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              className="h-10 rounded-lg px-3 text-sm font-semibold text-[#5A6478] hover:bg-[#F8FAFC]"
              onClick={() => onClose?.()}
              disabled={loading}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className="inline-flex h-10 min-w-[7.5rem] items-center justify-center rounded-lg bg-[#B81F25] px-4 text-sm font-semibold text-white disabled:opacity-60"
              onClick={() => onConfirm?.(reason)}
              disabled={loading}
            >
              {loading ? <span className="buttonLoader" /> : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
